import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import {Link} from 'gatsby'

import {BoxedContainer, Section} from '../../elements'
import {
  ExternalLinkPrimary,
  ExternalLinkSecondary,
} from '../../elements/Buttons'
import {TitleUppercaseStyle} from '../../elements/TitleUppercase'
import BrowserWindow from '../BrowserWindow'

import Arrow from '../../images/icons/arrow-up-right.svg'
import SearchIcon from '../../images/inline-svgs/search.svg'

const ThemePageHeader = ({
  image,
  title,
  eyebrow,
  description,
  price,
  purchaseLink,
  demoLink,
  isFree,
  eyebrowLink,
}) => {
  return (
    <Section padding="both">
      <StyledBoxedContainer>
        <Image>
          <BrowserWindow
            image={image}
            alt="Chronicles ghost theme"
            colorTheme="light"
            enlargedTopBar
          />
          {demoLink && (
            <PreviewLink href={demoLink} target="_blank">
              <StyledSearchIcon />
              <PreviewLinkText>Live Preview</PreviewLinkText>
            </PreviewLink>
          )}
        </Image>
        <Content>
          {eyebrow && <Eyebrow to={eyebrowLink}>{eyebrow}</Eyebrow>}
          <Title>{title}</Title>
          {description && (
            <Description dangerouslySetInnerHTML={{__html: description}} />
          )}
          {price && (
            <>
              <PriceHeader>Price</PriceHeader>
              <Price>{price}</Price>
            </>
          )}
          {purchaseLink && (
            <ExternalLinkPrimary
              href={purchaseLink}
              large
              fullWidth
              style={{
                margin: '0 0 12px',
              }}
            >
              Buy Now
            </ExternalLinkPrimary>
          )}
          {demoLink && (
            <ExternalLinkSecondary
              href={demoLink}
              large
              fullWidth
              icon={Arrow}
              target="_blank"
            >
              View Demo
            </ExternalLinkSecondary>
          )}
        </Content>
      </StyledBoxedContainer>
    </Section>
  )
}

export default ThemePageHeader

ThemePageHeader.propTypes = {
  image: PropTypes.object,
  title: PropTypes.string.isRequired,
  eyebrow: PropTypes.string,
  description: PropTypes.string,
  price: PropTypes.string,
  purchaseLink: PropTypes.string,
  demoLink: PropTypes.string,
  isFree: PropTypes.bool,
  eyebrowLink: PropTypes.string,
}

const StyledBoxedContainer = styled(BoxedContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 40px;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-gap: 70px;
  }

  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`

const Image = styled.div`
  height: 320px;
  position: relative;

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    height: 400px;
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    height: 475px;
  }

  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    height: 550px;
  }
`

const PreviewLink = styled.a`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: opacity 0.1s ease-in-out;

  &:hover,
  &:focus {
    opacity: 1;
    color: ${props => props.theme.color.primary.base};
  }
`

const StyledSearchIcon = styled(SearchIcon)`
  width: 65px;
  height: 65px;
  background: rgb(59, 42, 102, 0.8);
  border-radius: 100%;
  padding: 20px;
  color: #fff;
`

const PreviewLinkText = styled.p`
  font-size: 1.8rem;
  background: #fff;
  padding: 10px 15px;
  border-radius: 6px;
  border: 2px solid ${props => props.theme.color.primary.base};
  position: absolute;
  bottom: 25px;
`

const Content = styled.div`
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 15px 0 0;
  }

  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    padding: 15px 70px 0 0;
  }
`

const Eyebrow = styled(Link)`
  ${TitleUppercaseStyle}
  margin: 0 0 8px;
  color: ${props => props.theme.color.text.base};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: 1.5rem;
  }
`

const Title = styled.h1`
  color: ${props => props.theme.color.primary.base};
  margin: 0 0 40px;
`

const Description = styled.p`
  line-height: 1.6;
  margin: 0 0 40px;
  font-size: 1.8rem;

  a {
    color: ${props => props.theme.color.text.base};
  }
`

const PriceHeader = styled.p`
  margin: 0 0 4px;
`

const Price = styled.p`
  color: ${props => props.theme.color.primary.base};
  font-size: 3.2rem;
  margin: 0 0 40px;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: 3.6rem;
  }
`
